import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Grid } from '@mantine/core';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { selectPatientOverviewDetails } from '@/store/selectors/patientProfilePageSelectors';
import { useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';

import IconButton from '@/ui/IconButton';
import LabeledField from '@/ui/LabeledField';
import SkeletonGrid from '@/ui/Skeleton';

import PatientEditModal from './patientEditModal';
import styles from './PatientOverview.module.scss';

const PatientOverview = () => {
  const { uuid: patientId } = useParams() as { uuid: string };

  const { isLoading, isError } = useGetPatientQuery(patientId);
  const patientDetails = useSelector((state: RootState) =>
    selectPatientOverviewDetails(state, patientId)
  );
  const [editOpen, setEditOpen] = useState(false);

  const handleModalClose = () => {
    setEditOpen(false);
  };

  if (isLoading) {
    return (
      <>
        <SkeletonGrid columns={1} rows={1} height={20} />
        <SkeletonGrid columns={3} rows={4} height={30} />
      </>
    );
  }

  if (isError) return <div>Error loading patient information</div>;
  if (!patientDetails) return <div>No patient information available</div>;

  const fields = [
    { label: 'FIRST NAME', value: patientDetails.firstName },
    { label: 'LAST NAME', value: patientDetails.lastName },
    { label: 'PREFERRED NAME', value: patientDetails.preferredName },
    { label: 'DATE OF BIRTH', value: patientDetails.dateOfBirth },
    { label: 'ADDRESS 1', value: patientDetails.address1 },
    { label: 'ADDRESS 2', value: patientDetails.address2 },
    { label: 'CITY', value: patientDetails.city },
    { label: 'STATE', value: patientDetails.state },
    { label: 'ZIP', value: patientDetails.zip },
    { label: 'MOBILE PHONE', value: patientDetails.phoneNumber },
    { label: 'HOME PHONE', value: patientDetails.homePhone },
    { label: 'OTHER PHONE', value: patientDetails.otherPhone }
  ];

  return (
    <div>
      <div className={styles.editIcon}>
        <IconButton icon="edit" label="Edit" onClick={() => setEditOpen(true)} />
      </div>

      <Grid mt="md">
        {fields.slice(0, 4).map((field, index) => (
          <Grid.Col key={index} span={{ base: 12, sm: 6, md: 2.4 }}>
            <LabeledField label={field.label} value={field.value ?? '-'} />
          </Grid.Col>
        ))}

        <Grid.Col span={{ base: 0, sm: 0, md: 2.4 }} />

        {fields.slice(4).map((field, index) => (
          <Grid.Col key={index + 4} span={{ base: 12, sm: 6, md: 2.4 }}>
            <LabeledField label={field.label} value={field.value ?? '-'} />
          </Grid.Col>
        ))}
      </Grid>

      <PatientEditModal isOpen={editOpen} onClose={handleModalClose} patient={patientDetails} />
    </div>
  );
};

export default PatientOverview;
